import {
  Address,
  beginCell,
  Builder,
  Cell,
  Contract,
  contractAddress,
  ContractProvider,
  Sender,
  SendMode,
  toNano,
} from "ton-core";
import { NftData } from "./NftProposal";
import { readOnchainMetadata } from "./onchainContent";

export type DaoConfig = {
  quorumNominator: number;
  quorumDenominator: number;
  royalty: Cell;
  content: Cell;
  codes: Cell;
};

export interface DaoData extends NftData {
  symbol: string;
  decimals: string;
}

export interface DaoContractData {
  totalSupply: bigint;
  nextProposalId: bigint;
  quorumNominator: number;
  quorumDenominator: number;
  content: Cell;
  daoContent: DaoData;
}

export function daoConfigToCell(config: DaoConfig): Cell {
  return beginCell()
    .storeCoins(0) // total_supply
    .storeUint(0, 64) // proposal id
    .storeUint(config.quorumNominator, 8)
    .storeUint(config.quorumDenominator, 8)
    .storeRef(config.royalty)
    .storeRef(config.content)
    .storeRef(config.codes)
    .endCell();
}

export class Dao implements Contract {
  constructor(
    readonly address: Address,
    readonly init?: { code: Cell; data: Cell }
  ) {}

  static createFromAddress(address: Address) {
    return new Dao(address);
  }

  static createFromConfig(config: DaoConfig, code: Cell, workchain = 0) {
    const data = daoConfigToCell(config);
    const init = { code, data };
    return new Dao(contractAddress(workchain, init), init);
  }

  async sendDeploy(provider: ContractProvider, via: Sender, value: bigint) {
    await provider.internal(via, {
      value,
      sendMode: SendMode.PAY_GAS_SEPARATELY,
      body: beginCell().endCell(),
    });
  }

  static sendMessageRef(address: Address, amount: bigint) {
    return beginCell()
      .storeUint(0x10, 6)
      .storeAddress(address)
      .storeCoins(amount)
      .storeUint(1, 1 + 4 + 4 + 64 + 32 + 1 + 1);
  }

  static sendMessageSlice(address: Address, amount: bigint) {
    return beginCell()
      .storeUint(0x10, 6)
      .storeAddress(address)
      .storeCoins(amount)
      .storeUint(0, 1 + 4 + 4 + 64 + 32 + 1 + 1);
  }

  static selfMessage(daoAddress: Address) {
    return this.sendMessageSlice(daoAddress, toNano("0.05"));
  }

  static sendTonMessage(address: Address, amount: bigint, comment?: string) {
    let message = beginCell()
      .storeUint(0x10, 6)
      .storeAddress(address)
      .storeCoins(amount)
      .storeUint(0, 1 + 4 + 4 + 64 + 32 + 1 + 1);

    if (comment) {
      message = message.storeUint(0, 32).storeStringTail(comment);
    }

    return message;
  }

  static defaultForwardAmount = toNano("0.002");

  static sendJettonMessage(params: {
    queryId?: number;
    jettonAmount: bigint;
    toAddress: Address;
    responseAddress: Address;
    forwardAmount?: bigint;
    forwardPayload?: Builder | null;
  }) {
    return beginCell()
      .storeUint(0xf8a7ea5, 32) // request_transfer op
      .storeUint(params.queryId || Date.now(), 64)
      .storeCoins(params.jettonAmount)
      .storeAddress(params.toAddress)
      .storeAddress(params.responseAddress) // responseAddress
      .storeBit(false) // null custom_payload
      .storeCoins(params.forwardAmount ?? this.defaultForwardAmount)
      .storeBit(false) // forward_payload in this slice, not separate cell
      .storeMaybeBuilder(params.forwardPayload)
      .endCell();
  }

  static sendNftMessage(params: {
    queryId?: number;
    newOwnerAddress: Address;
    responseAddress: Address;
    forwardAmount?: bigint;
    forwardPayload?: Builder | null;
  }) {
    return beginCell()
      .storeUint(0x5fcc3d14, 32) // transfer op
      .storeUint(params.queryId || 0, 64)
      .storeAddress(params.newOwnerAddress)
      .storeAddress(params.responseAddress) // responseAddress
      .storeBit(false) // null custom_payload
      .storeCoins(params.forwardAmount ?? this.defaultForwardAmount)
      .storeBit(false) // forward_payload in this slice, not separate cell
      .storeMaybeBuilder(params.forwardPayload)
      .endCell();
  }

  static updateContentMessage(content: Cell, queryId: number = Date.now()) {
    return beginCell()
      .storeUint(0x5773d1f5, 32)
      .storeUint(queryId || 0, 64)
      .storeRef(content);
  }

  static updateQuorumMessage(
    quorumNominator: number,
    quorumDenominator: number,
    queryId: number = Date.now()
  ) {
    return beginCell()
      .storeUint(0x3247f5bb, 32)
      .storeUint(queryId || 0, 64)
      .storeUint(quorumNominator, 8)
      .storeUint(quorumDenominator, 8);
  }

  static mintTokensMessage(
    address: Address,
    jettonAmount: bigint,
    forwardTonAmount: bigint = this.defaultForwardAmount,
    totalTonAmount: bigint = toNano("0.1"),
    queryId: number = Date.now()
  ) {
    return beginCell()
      .storeUint(0x1674b0a0, 32)
      .storeUint(queryId, 64)
      .storeAddress(address)
      .storeCoins(jettonAmount)
      .storeCoins(forwardTonAmount)
      .storeCoins(totalTonAmount);
  }

  async sendInitMessage(
    provider: ContractProvider,
    via: Sender,
    value: bigint,
    options: {
      to: Address;
      jettonAmount: bigint;
      forwardTonAmount: bigint;
      totalTonAmount: bigint;
    }
  ) {
    await provider.internal(via, {
      value,
      sendMode: SendMode.PAY_GAS_SEPARATELY,
      body: beginCell()
        .storeUint(0x24060fa9, 32)
        .storeUint(0, 64)
        .storeAddress(options.to)
        .storeCoins(options.jettonAmount)
        .storeCoins(options.forwardTonAmount)
        .storeCoins(options.totalTonAmount)
        .endCell(),
    });
  }

  async getWalletAddress(
    provider: ContractProvider,
    owner: Address
  ): Promise<Address> {
    const res = await provider.get("get_wallet_address", [
      { type: "slice", cell: beginCell().storeAddress(owner).endCell() },
    ]);
    return res.stack.readAddress();
  }

  async getNftProposalAddress(
    provider: ContractProvider,
    value: bigint
  ): Promise<Address> {
    const res = await provider.get("get_nft_address_by_index", [
      { type: "int", value },
    ]);
    return res.stack.readAddress();
  }

  async getJettonData(provider: ContractProvider) {
    let res = await provider.get("get_jetton_data", []);
    let totalSupply = res.stack.readBigNumber();
    let mintable = res.stack.readBoolean();
    let daoAddress = res.stack.readAddress();
    let content = res.stack.readCell();
    let walletCode = res.stack.readCell();
    return {
      totalSupply,
      mintable,
      daoAddress,
      content,
      walletCode,
    };
  }

  async getCollectionData(provider: ContractProvider) {
    let res = await provider.get("get_collection_data", []);
    const nextProposalId = res.stack.readBigNumber();
    const content = res.stack.readCell();
    const daoAddress = res.stack.readAddress();
    return {
      nextProposalId,
      content,
      daoAddress,
    };
  }

  async getDaoData(provider: ContractProvider): Promise<DaoContractData> {
    let res = await provider.get("dao_params", []);
    let totalSupply = res.stack.readBigNumber();
    const nextProposalId = res.stack.readBigNumber();
    const quorumNominator = res.stack.readNumber();
    const quorumDenominator = res.stack.readNumber();
    const content = res.stack.readCell();

    const daoContent = readOnchainMetadata<DaoData>(content, [
      "name",
      "image",
      "description",
      "symbol",
      "decimals",
    ]);

    return {
      totalSupply,
      nextProposalId,
      quorumNominator,
      quorumDenominator,
      content,
      daoContent,
    };
  }

  async getTotalSupply(provider: ContractProvider) {
    const { totalSupply } = await this.getDaoData(provider);
    return totalSupply;
  }

  static discoveryMessage(owner: Address, include_address: boolean) {
    return beginCell()
      .storeUint(0x2c76b973, 32)
      .storeUint(0, 64) // op, queryId
      .storeAddress(owner)
      .storeBit(include_address)
      .endCell();
  }

  async sendDiscovery(
    provider: ContractProvider,
    via: Sender,
    owner: Address,
    include_address: boolean,
    value: bigint = toNano("0.1")
  ) {
    await provider.internal(via, {
      sendMode: SendMode.PAY_GAS_SEPARATELY,
      body: Dao.discoveryMessage(owner, include_address),
      value,
    });
  }

  static scheduleProposalMessage(
    expirationDate: Date,
    proposalContent: Cell,
    queryId: number = Date.now()
  ) {
    return beginCell()
      .storeUint(0x725d9de2, 32)
      .storeUint(queryId, 64) // op, queryId
      .storeUint(Math.round(expirationDate.getTime() / 1000), 48)
      .storeRef(proposalContent)
      .endCell();
  }
}
